import type { User } from "@trader/shared/AuthApi"
import { lazy, Suspense, useEffect, useState } from "react"
import { useLive } from "../../live.ts"
import { logout } from "../auth/session.ts"
import { drawingsStore } from "../drawings/drawingsStore.ts"
import { ErrorBoundary } from "../../ui/ErrorBoundary.tsx"
import { IndicatorsIcon, WatchlistIcon } from "../../ui/icons.tsx"
import { BrokerStatus } from "../brokers/BrokerStatus.tsx"
import { ChartPane } from "../chart/ChartPane.tsx"
import { DrawingToolbar } from "../drawings/DrawingToolbar.tsx"
import { FavoriteTools } from "../drawings/FavoriteTools.tsx"
import { toolFavorites } from "../drawings/toolFavorites.ts"
import { IndicatorsDialog, type IndicatorsDialogTab } from "../indicators/IndicatorsDialog.tsx"
import { BarReplayBar, ReplayStrip } from "../replay/BarReplayBar.tsx"
import { SymbolSearch } from "../instruments/SymbolSearch.tsx"
import { useInstrument } from "../instruments/useInstrument.ts"
import { myStrategies } from "../strategies/myStrategies.ts"
import { StrategyTester } from "../strategies/StrategyTester.tsx"
import { userDrafts } from "../strategies/userDrafts.ts"
import { strategyEditor, useStrategyEditorPanel } from "../strategy-editor/editorPanel.ts"
import { useTesterCollapsed } from "../strategies/testerPanel.ts"
import { accountsStore } from "../trading/tradingData.ts"
import { Watchlist } from "../watchlist/Watchlist.tsx"
import { useAddToWatchlistShortcut, useWatchlistOpen, watchlist, watchlistPanel } from "../watchlist/watchlistStore.ts"
import { LayoutPicker } from "./LayoutPicker.tsx"
import { layoutById, PANE_AREAS } from "./layouts.ts"
import { TimeframePicker } from "./TimeframePicker.tsx"
import { discardWorkspaceSaves, flushWorkspace, setActivePane, syncWorkspaceFromServer, useWorkspace } from "./workspaceStore.ts"

// Monaco and the editor load only when the editor first opens (their own chunk).
const StrategyEditor = lazy(() => import("../strategy-editor/StrategyEditor.tsx"))
// The paper-trading panel (tables, dialogs, order ticket) is its own chunk; its
// header placeholder shows while it loads. The chart side (lines, replay clipping) stays in the main bundle.
const TradingPanel = lazy(() => import("../trading/TradingPanel.tsx").then((module) => ({ default: module.TradingPanel })))

function TradingPanelPlaceholder({ failed, onRetry }: { readonly failed?: boolean; readonly onRetry?: () => void }) {
  return (
    <section className="trading-panel" aria-label="Paper trading">
      <header className="tester-header trading-header">
        <span className="tester-title">
          <span className="tester-chevron">▴</span>
          Paper Trading
        </span>
        {failed && (
          <span className="down">
            Couldn't load paper trading.{" "}
            <button type="button" className="link" onClick={onRetry}>Retry</button>
          </span>
        )}
      </header>
    </section>
  )
}

/** The Fyers login redirect lands on /?fyers=connected or /?fyers=error&reason=…. */
const useBrokerRedirectNotice = () => {
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const outcome = params.get("fyers")
    if (outcome === null) return
    setNotice(outcome === "connected"
      ? { ok: true, text: "Fyers connected." }
      : { ok: false, text: `Fyers: ${params.get("reason") ?? "login failed"}` })
    window.history.replaceState(null, "", window.location.pathname)
  }, [])
  return [notice, () => setNotice(null)] as const
}

function LatencyBadge() {
  const live = useLive()
  const label = live.status === "open" && live.rttMs !== null ? `${live.rttMs.toFixed(0)} ms` : live.status
  return <span className={`latency latency-${live.status}`} title="Round trip to the server">{label}</span>
}

/** Pending saves (drawings, workspace) go out first, for at most a few seconds, while the session is still valid. */
const signOut = async () => {
  await Promise.race([Promise.all([drawingsStore.flush(), flushWorkspace(), watchlist.flush(), toolFavorites.flush()]), new Promise((resolve) => setTimeout(resolve, 3_000))])
  await logout()
}

export function Workspace({ user }: { readonly user: User }) {
  const workspace = useWorkspace()
  const layout = layoutById(workspace.layout)
  // Always a shown pane (workspaceStore keeps it so).
  const activeIndex = workspace.active
  const active = workspace.panes[activeIndex]!
  const activeInstrument = useInstrument(active.instrumentId)
  const [notice, dismissNotice] = useBrokerRedirectNotice()
  // The Indicators & strategies dialog, and the tab it opens on.
  const [dialogTab, setDialogTab] = useState<IndicatorsDialogTab | null>(null)
  const shownPanes = workspace.panes.slice(0, layout.panes)
  const editor = useStrategyEditorPanel()
  const anyStrategy = shownPanes.some((pane) => pane.strategy != null)
  const testerCollapsed = useTesterCollapsed()
  const testerOpen = anyStrategy && !testerCollapsed

  // The workspace saved on the server wins over this browser's copy.
  useEffect(() => void syncWorkspaceFromServer(String(user.id)), [user.id])
  // The user's watchlist (docs/design/0014), likewise.
  useEffect(() => void watchlist.signedIn(String(user.id)), [user.id])
  useEffect(() => void toolFavorites.signedIn(String(user.id)), [user.id])
  const watchlistOpen = useWatchlistOpen()
  // Alt+W: the active chart's symbol into the watchlist.
  useAddToWatchlistShortcut(active.instrumentId)
  // The user's paper accounts: charts need to know early whether they follow a replay.
  useEffect(() => accountsStore.start(user.id), [user.id])
  // Drawings belong to this user: another user signing in starts from the server's copies.
  useEffect(() => () => {
    drawingsStore.reset()
    discardWorkspaceSaves()
    myStrategies.reset()
    userDrafts.reset()
  }, [user.id])
  useEffect(() => strategyEditor.signedIn(user.id), [user.id])

  return (
    <div className="workspace">
      <header className="toolbar">
        <span className="brand">Trader</span>
        <SymbolSearch />
        <TimeframePicker current={active.timeframe} />
        <button
          type="button"
          className="toolbar-button"
          title="Indicators & strategies"
          onClick={() => setDialogTab("indicators")}
        >
          <IndicatorsIcon />
          Indicators
        </button>
        <LayoutPicker current={layout.id} />
        <BarReplayBar />
        <span className="toolbar-spacer" />
        <button
          type="button"
          className={watchlistOpen ? "toolbar-button toolbar-button-on" : "toolbar-button"}
          title={watchlistOpen ? "Hide the watchlist (Alt+W adds the chart's symbol)" : "Show the watchlist (Alt+W adds the chart's symbol)"}
          aria-pressed={watchlistOpen}
          onClick={() => watchlistPanel.set(!watchlistOpen)}
        >
          <WatchlistIcon />
          Watchlist
        </button>
        <LatencyBadge />
        <BrokerStatus />
        <span className="user">{user.username}</span>
        <button type="button" className="link" onClick={() => void signOut()}>Sign out</button>
      </header>
      <ReplayStrip />

      {notice && (
        <div className={notice.ok ? "notice notice-ok" : "notice notice-error"} role="status">
          {notice.text}
          <button type="button" className="link" onClick={dismissNotice}>Dismiss</button>
        </div>
      )}

      <div className="workspace-body">
        <DrawingToolbar />
        <FavoriteTools />
        <main className="panes" style={{ gridTemplateAreas: layout.areas }}>
          {shownPanes.map((pane, index) => (
            <ChartPane
              key={index}
              index={index}
              pane={pane}
              area={PANE_AREAS[index]!}
              active={index === activeIndex}
              highlighted={layout.panes > 1 && index === activeIndex}
              onActivate={() => setActivePane(index)}
            />
          ))}
        </main>
        {editor.open && (
          <ErrorBoundary
            fallback={(error, reset) => (
              <aside className="strategy-editor">
                <p className="down editor-message">The strategy editor stopped: {error.message}</p>
                <p className="editor-message">
                  <button type="button" onClick={reset}>Try again</button>{" "}
                  <button type="button" className="link" onClick={strategyEditor.close}>Close</button>
                </p>
              </aside>
            )}
          >
            <Suspense fallback={<aside className="strategy-editor"><p className="muted editor-message">Loading the editor…</p></aside>}>
              <StrategyEditor
                pane={activeIndex}
                paneStrategyId={active.strategy?.id ?? null}
                chartLabel={`${active.instrumentId} · ${active.timeframe}`}
              />
            </Suspense>
          </ErrorBoundary>
        )}
        {watchlistOpen && (
          <ErrorBoundary fallback={(error) => <aside className="watchlist"><p className="down watch-empty">The watchlist stopped: {error.message}</p></aside>}>
            <Watchlist />
          </ErrorBoundary>
        )}
      </div>

      <StrategyTester
        pane={activeIndex}
        strategy={active.strategy}
        instrument={activeInstrument.status === "ready" ? activeInstrument.instrument : null}
        timeframe={active.timeframe}
        chartLabel={`${active.instrumentId} · ${active.timeframe}`}
        anyStrategy={anyStrategy}
        onAddStrategy={() => setDialogTab("strategies")}
      />

      <ErrorBoundary fallback={(_error, retry) => <TradingPanelPlaceholder failed onRetry={retry} />}>
        <Suspense fallback={<TradingPanelPlaceholder />}>
          <TradingPanel
            userId={user.id}
            instrument={activeInstrument.status === "ready" ? activeInstrument.instrument : null}
            timeframe={active.timeframe}
            testerOpen={testerOpen}
          />
        </Suspense>
      </ErrorBoundary>

      {dialogTab && <IndicatorsDialog initialTab={dialogTab} onClose={() => setDialogTab(null)} />}
    </div>
  )
}
